/**
 * Utilities for safely converting rich text (HTML or HTML-escaped HTML) to plain text.
 *
 * We use this in places like list/card previews where we want a clean snippet
 * and must never show raw tags like <p> or <strong>.
 */

const decodeHtmlEntities = (input: string): string => {
  if (!input) return "";

  // Browser environment
  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = input;
    return textarea.value;
  }

  // Fallback (should rarely be used in this app)
  return input
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#39;/g, "'");
};

const stripHtmlTags = (input: string): string =>
  input
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Converts HTML / escaped HTML into plain text.
 * Handles double-encoded content like &amp;lt;p&amp;gt;.
 */
export const toPlainText = (content: string | null | undefined): string => {
  if (!content) return "";

  let text = String(content);

  // 2 passes: decode -> strip -> decode -> strip
  // This covers both normal HTML (<p>..) and HTML-escaped HTML (&lt;p&gt;..).
  for (let i = 0; i < 2; i += 1) {
    text = decodeHtmlEntities(text);
    text = stripHtmlTags(text);
  }

  return text;
};
